/**
 * TeenPattiTable — Circular table with player positions, pot, dealer marker.
 */
import React from 'react';
import { TeenPattiTableProps, VisibleTeenPattiPlayerState } from '../types';
import { TeenPattiCards } from './TeenPattiCards';
import './TeenPattiTable.css';

const SEAT_POSITIONS: Record<number, { top: string; left: string }[]> = {
  3: [
    { top: '80%', left: '50%' },
    { top: '20%', left: '15%' },
    { top: '20%', left: '85%' },
  ],
  4: [
    { top: '85%', left: '50%' },
    { top: '50%', left: '5%' },
    { top: '15%', left: '50%' },
    { top: '50%', left: '95%' },
  ],
  5: [
    { top: '85%', left: '50%' },
    { top: '60%', left: '5%' },
    { top: '15%', left: '25%' },
    { top: '15%', left: '75%' },
    { top: '60%', left: '95%' },
  ],
  6: [
    { top: '85%', left: '50%' },
    { top: '60%', left: '5%' },
    { top: '20%', left: '10%' },
    { top: '20%', left: '90%' },
    { top: '60%', left: '95%' },
    { top: '15%', left: '50%' },
  ],
};

const PlayerSeat: React.FC<{
  player: VisibleTeenPattiPlayerState;
  isCurrentPlayer: boolean;
  isActivePlayer: boolean;
  isHumanPlayer: boolean;
  position: { top: string; left: string };
}> = ({ player, isCurrentPlayer, isActivePlayer, isHumanPlayer, position }) => {
  const isOut = player.status === 'eliminated';
  const isPacked = player.status === 'packed';

  return (
    <div
      className={`tp-table__seat ${isActivePlayer ? 'tp-table__seat--active' : ''} ${isOut ? 'tp-table__seat--eliminated' : ''} ${isPacked ? 'tp-table__seat--packed' : ''}`}
      style={{ top: position.top, left: position.left }}
      role="group"
      aria-label={`${player.name}'s seat`}
    >
      {player.isDealer && <span className="tp-table__dealer-marker" aria-label="Dealer">D</span>}
      <div className="tp-table__player-info">
        <span className="tp-table__player-name">{player.name}</span>
        <span className={`tp-table__blind-seen ${player.blindSeenStatus === 'blind' ? 'tp-table__blind-seen--blind' : 'tp-table__blind-seen--seen'}`}>
          {player.blindSeenStatus === 'blind' ? '🔒' : '👁'}
        </span>
      </div>
      <div className="tp-table__cards">
        <TeenPattiCards
          cards={isHumanPlayer && player.cards ? player.cards : null}
          size="small"
          faceDown={!isHumanPlayer || !player.cards}
          isBlind={player.blindSeenStatus === 'blind'}
        />
      </div>
      <div className="tp-table__chips">
        💰 {player.chipStack}
      </div>
      {player.currentBetThisRound > 0 && (
        <div className="tp-table__bet">Bet: {player.currentBetThisRound}</div>
      )}
      {isOut && <div className="tp-table__out-badge">Out</div>}
      {isPacked && <div className="tp-table__packed-badge">Packed</div>}
    </div>
  );
};

const TeenPattiTable: React.FC<TeenPattiTableProps> = ({
  gameState, currentPlayerId, isMultiplayer,
}) => {
  const playerCount = gameState.players.length;
  const positions = SEAT_POSITIONS[playerCount] || SEAT_POSITIONS[6];
  const currentTurnPlayer = gameState.players[gameState.currentPlayerIndex];

  return (
    <div className="tp-table" role="region" aria-label="Teen Patti table">
      <div className="tp-table__felt">
        {/* Pot display */}
        <div className="tp-table__pot" aria-live="polite">
          <span className="tp-table__pot-label">Pot</span>
          <span className="tp-table__pot-amount">{gameState.pot}</span>
          <span className="tp-table__stake-label">Stake: {gameState.currentStake}</span>
        </div>

        {/* Player seats */}
        {gameState.players.map((player, i) => {
          const pos = positions[i] || { top: '50%', left: '50%' };
          return (
            <PlayerSeat
              key={player.id}
              player={player}
              isCurrentPlayer={player.id === currentPlayerId}
              isActivePlayer={currentTurnPlayer?.id === player.id}
              isHumanPlayer={player.id === currentPlayerId}
              position={pos}
            />
          );
        })}
      </div>
    </div>
  );
};

export { TeenPattiTable };
export default TeenPattiTable;
